'use client';

/**
 * Zentrale Google-Ads-Anbindung: Consent, gtag-Bootstrap und Page-Views.
 *
 * Next.js navigiert clientseitig. Beim Wechsel auf eine /download-Seite lädt also
 * kein neues Dokument, und gtag.js meldet von sich aus keinen Seitenaufruf. Damit
 * URL-basierte Conversion-Regeln in Google Ads ("URL enthält /download") greifen
 * können, wird der Page-View hier bei jedem Routenwechsel explizit gesendet.
 *
 * Wichtig: Ein erneutes `gtag('config', ...)` für dieselbe ID erzeugt KEINEN
 * neuen Page-View – deshalb `send_page_view: false` plus ein manuelles
 * page_view-Event.
 */

type GtagArguments = [command: string, ...args: unknown[]];

declare global {
    interface Window {
        dataLayer?: unknown[];
        gtag?: (...args: GtagArguments) => void;
    }
}

export type ConsentValue = 'granted' | 'denied';

const CONSENT_KEY = 'google-ads-consent-v1';

const CLICK_ID_PARAMS = ['gclid', 'gbraid', 'wbraid'] as const;

/** Query-Parameter, die niemals an Google übertragen werden dürfen. */
const SENSITIVE_QUERY_PARAMS = ['token'];

const DENIED_CONSENT = {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
} as const;

const GRANTED_CONSENT = {
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
} as const;

let initialized = false;

/*
 * Die Klick-ID wird nur im Speicher gehalten, nicht persistiert: Vor der
 * Zustimmung darf keine Marketing-Kennung in den Storage geschrieben werden. Für
 * den relevanten Fall (Anzeige -> Landingpage -> Formular -> /download) genügt
 * das, weil das alles ein einziges Dokument ist.
 */
let clickId: { key: string; value: string } | null = null;

function isValidAdsId(adsId: string): boolean {
    return /^AW-\d+$/.test(adsId);
}

export function readConsent(): ConsentValue | null {
    try {
        const value = window.localStorage.getItem(CONSENT_KEY);
        return value === 'granted' || value === 'denied' ? value : null;
    } catch {
        return null;
    }
}

export function saveConsent(value: ConsentValue): void {
    try {
        window.localStorage.setItem(CONSENT_KEY, value);
    } catch {
        /* Auswahl gilt dann nur für den aktuellen Seitenaufruf. */
    }
}

function ensureGtag(): void {
    window.dataLayer ??= [];

    if (!window.gtag) {
        /*
         * Bewusst das `arguments`-Objekt pushen statt eines Arrays – gtag.js
         * erwartet exakt diese Struktur (so steht es im offiziellen Snippet).
         */
        window.gtag = function gtag() {
            // eslint-disable-next-line prefer-rest-params
            window.dataLayer?.push(arguments);
        } as unknown as (...args: GtagArguments) => void;
    }
}

/**
 * Merkt sich die Klick-ID aus der aktuellen URL. Muss so früh wie möglich laufen:
 * Nach einer clientseitigen Navigation ist die gclid aus der URL verschwunden.
 */
export function rememberClickId(): void {
    if (clickId) return;

    const params = new URLSearchParams(window.location.search);

    for (const key of CLICK_ID_PARAMS) {
        const value = params.get(key);

        if (value) {
            clickId = { key, value };
            return;
        }
    }
}

/**
 * URL für `page_location`: ohne interne Tokens, aber mit der ursprünglichen
 * Klick-ID. Google liest die gclid aus `page_location` – fehlt sie, lässt sich
 * die Conversion keinem Anzeigenklick zuordnen.
 */
function buildPageLocation(): string {
    const url = new URL(window.location.href);

    for (const param of SENSITIVE_QUERY_PARAMS) {
        url.searchParams.delete(param);
    }

    const hasClickId = CLICK_ID_PARAMS.some((param) => url.searchParams.has(param));

    if (clickId && !hasClickId) {
        url.searchParams.set(clickId.key, clickId.value);
    }

    return url.toString();
}

function loadGtagScript(adsId: string): void {
    const existing = document.querySelector<HTMLScriptElement>(
        'script[src^="https://www.googletagmanager.com/gtag/js"]',
    );

    // gtag.js wurde eventuell schon von einem anderen Google-Tag geladen.
    if (existing) return;

    const script = document.createElement('script');

    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(adsId)}`;

    document.head.appendChild(script);
}

/**
 * Baut den dataLayer auf und lädt gtag.js. Reihenfolge ist entscheidend:
 * consent default -> js -> config. Idempotent.
 */
function initGoogleAds(adsId: string): void {
    if (initialized) return;

    if (!isValidAdsId(adsId)) {
        console.error(`Ungültige Google-Ads-ID: ${adsId}`);
        return;
    }

    initialized = true;

    ensureGtag();

    window.gtag?.('consent', 'default', DENIED_CONSENT);
    window.gtag?.('js', new Date());

    window.gtag?.('config', adsId, {
        // Page-Views werden manuell gesendet – auch der erste, siehe trackPageView().
        send_page_view: false,
        page_location: buildPageLocation(),
    });

    loadGtagScript(adsId);
}

export function grantAdsConsent(adsId: string): void {
    initGoogleAds(adsId);

    window.gtag?.('consent', 'update', GRANTED_CONSENT);
}

export function denyAdsConsent(): void {
    // Ohne Zustimmung wurde gtag.js nie geladen – dann gibt es nichts zu widerrufen.
    window.gtag?.('consent', 'update', DENIED_CONSENT);
}

/**
 * Meldet einen Seitenaufruf an Google Ads. Das ist der Hit, gegen den Google die
 * URL-Regeln der Conversion-Aktionen prüft.
 */
export function trackPageView(adsId: string): void {
    window.gtag?.('event', 'page_view', {
        send_to: adsId,
        page_location: buildPageLocation(),
        page_title: document.title,
    });
}
